import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Star, TrendingUp, FileText, Download, FileSpreadsheet, Pencil,
  DollarSign, Calendar, BarChart3, Award, Tag, Settings, CheckCircle2,
  CreditCard, Receipt, Building, Shield, ArrowUpCircle, XCircle,
  Clock, AlertTriangle, Mail, ShieldCheck, Plug
} from 'lucide-react';
import axios from 'axios';
import ApiIntegrationTab from '@/components/portal/ApiIntegrationTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Subscription State Colors ───────────────────────────────────────
const stateConfig = {
  STANDARD_FREE: { label: 'Standard (Kostenlos)', color: 'bg-slate-100 text-slate-700', icon: Shield },
  TRIAL: { label: 'Testphase', color: 'bg-blue-100 text-blue-700', icon: Clock },
  PENDING_PAYMENT: { label: 'Zahlung ausstehend', color: 'bg-amber-100 text-amber-700', icon: CreditCard },
  ACTIVE_PREMIUM: { label: 'Premium Aktiv', color: 'bg-emerald-100 text-emerald-700', icon: Star },
  PAST_DUE: { label: 'Zahlung überfällig', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  SUSPENDED: { label: 'Gesperrt', color: 'bg-red-200 text-red-800', icon: XCircle },
  CANCELLED_ACTIVE: { label: 'Gekündigt (aktiv)', color: 'bg-orange-100 text-orange-700', icon: Clock },
  CANCELLED_EXPIRED: { label: 'Gekündigt (abgelaufen)', color: 'bg-gray-100 text-gray-600', icon: XCircle },
  PAUSED: { label: 'Pausiert', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  DOWNGRADED: { label: 'Herabgestuft', color: 'bg-slate-100 text-slate-600', icon: Shield },
  BLOCKED: { label: 'Blockiert', color: 'bg-red-300 text-red-900', icon: XCircle },
};

const StatCard = ({ label, value, sub, icon: Icon, accent = 'orange' }) => {
  const bg = { orange: 'bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400', blue: 'bg-blue-50 text-blue-600 dark:bg-blue-950', green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950' };
  return (
    <Card>
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold mt-1 font-mono">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2.5 rounded-lg ${bg[accent]}`}><Icon className="h-5 w-5" /></div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Commission Tab (existing logic) ─────────────────────────────────

const CommissionTab = ({ summary, txns, page, setPage, poDialog, setPoDialog, poValue, setPoValue, handleSavePO, savingPO }) => {
  const isPremium = summary?.seller_tier === 'premium';
  const ruleTypeLabel = (t) => ({ seller: 'Verkäufer', category: 'Kategorie', volume: 'Volumen', time_period: 'Zeitraum' }[t] || t);

  const exportCsv = () => {
    const params = new URLSearchParams({ export_type: 'transactions' });
    window.open(`${API}/commission/export/csv?${params}`, '_blank');
  };
  const exportPdf = () => {
    const params = new URLSearchParams({ export_type: 'transactions' });
    window.open(`${API}/commission/export/pdf?${params}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Tier Info */}
      <div className={`rounded-xl p-4 border-2 ${isPremium ? 'border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-700' : 'border-slate-200 bg-slate-50 dark:bg-slate-900 dark:border-slate-700'}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isPremium ? 'bg-amber-100 dark:bg-amber-900' : 'bg-slate-100 dark:bg-slate-800'}`}>
              <Award className={`h-5 w-5 ${isPremium ? 'text-amber-600' : 'text-slate-500'}`} />
            </div>
            <div>
              <p className="font-semibold">{isPremium ? 'Premium-Mitglied' : 'Standard-Mitglied'}</p>
              <p className="text-sm text-muted-foreground">
                Provisionssatz: <span className="font-mono font-bold text-orange-600">{isPremium ? summary?.premium_rate : summary?.default_rate}%</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Transaktionen" value={summary?.total_transactions || 0} icon={FileText} accent="blue" />
        <StatCard label="Gesamtumsatz" value={`${(summary?.total_sales_eur || 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })} EUR`} sub="alle Transaktionen" icon={TrendingUp} accent="orange" />
        <StatCard label="Provision gezahlt" value={`${(summary?.total_commission_eur || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR`} icon={DollarSign} accent="orange" />
        <StatCard label="Eff. Durchschnittsrate" value={`${summary?.avg_effective_rate || 0}%`} sub="gewichteter Schnitt" icon={BarChart3} accent="green" />
      </div>

      {/* Active Rules + Monthly */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Settings className="h-4 w-4 text-muted-foreground" />Aktive Sonderregeln</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.active_rules?.length ? (
              <div className="space-y-2">
                {summary.active_rules.slice(0, 5).map(r => (
                  <div key={r.rule_id} className="flex items-center justify-between py-1 border-b last:border-0">
                    <div>
                      <p className="text-xs font-medium">{r.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{ruleTypeLabel(r.rule_type)}</p>
                    </div>
                    <span className="font-mono text-sm font-bold text-orange-600">{r.commission_rate}%</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">Keine Sonderregeln aktiv.</p>}
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" />Monatliche Übersicht</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(summary?.monthly_breakdown || []).slice(0, 6).map(m => (
                <div key={m.month} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-16 shrink-0 font-mono">{m.month}</span>
                  <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                    <div className="h-full bg-orange-400 rounded-full" style={{ width: `${Math.min(100, (m.commission_eur / (Math.max(...(summary?.monthly_breakdown || []).map(x => x.commission_eur), 1)) * 100))}%` }} />
                  </div>
                  <span className="text-xs font-mono w-24 text-right">{m.commission_eur.toFixed(2)} EUR</span>
                  <span className="text-xs text-muted-foreground w-10 text-right">{m.transactions} Txn</span>
                </div>
              ))}
              {!summary?.monthly_breakdown?.length && <p className="text-xs text-muted-foreground">Noch keine Transaktionen</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PO Number */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">PO-Nummer</p>
              <p className="text-xs text-muted-foreground mt-0.5">Wird auf alle Provisionsberichte gedruckt</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm font-medium" data-testid="po-number-display">{summary?.po_number || <span className="text-muted-foreground italic">Nicht gesetzt</span>}</span>
              <Button size="sm" variant="outline" onClick={() => { setPoValue(summary?.po_number || ''); setPoDialog(true); }} data-testid="edit-po-btn">
                <Pencil className="h-3.5 w-3.5 mr-1" />Bearbeiten
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transaktionshistorie</CardTitle>
              <CardDescription>{txns.total} Transaktionen gesamt</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-1" onClick={exportCsv} data-testid="txn-export-csv"><FileSpreadsheet className="h-3.5 w-3.5" />CSV</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={exportPdf} data-testid="txn-export-pdf"><FileText className="h-3.5 w-3.5" />PDF</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium text-muted-foreground">Datum</th>
                  <th className="pb-2 font-medium text-muted-foreground">Material / Kategorie</th>
                  <th className="pb-2 font-medium text-muted-foreground">Betrag</th>
                  <th className="pb-2 font-medium text-muted-foreground">Rate</th>
                  <th className="pb-2 font-medium text-muted-foreground">Provision</th>
                  <th className="pb-2 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {(txns.items || []).map(t => {
                  const gross = t.price_EUR || (t.value_kEUR * 1000) || 0;
                  return (
                    <tr key={t.transaction_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`txn-row-${t.transaction_id}`}>
                      <td className="py-2.5 text-xs text-muted-foreground font-mono">{(t.created_at || t.executed_at || '').substring(0, 10)}</td>
                      <td className="py-2.5 max-w-[160px]">
                        <p className="text-xs font-medium truncate">{t.material_title || '–'}</p>
                        <p className="text-xs text-muted-foreground truncate">{t.category_name || '–'}</p>
                      </td>
                      <td className="py-2.5 font-mono text-sm">{Number(gross).toFixed(2)} EUR</td>
                      <td className="py-2.5 font-mono text-sm text-orange-600">{t.commission_rate}%</td>
                      <td className="py-2.5 font-mono font-bold text-sm">{Number(t.commission_amount || 0).toFixed(2)} EUR</td>
                      <td className="py-2.5">
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle2 className="h-3 w-3 mr-1" />Abgeschlossen
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
                {!txns.items?.length && (
                  <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">Noch keine Transaktionen</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {txns.pages > 1 && (
            <div className="flex gap-2 mt-3 justify-end">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Zurück</Button>
              <span className="text-sm text-muted-foreground py-2">{page}/{txns.pages}</span>
              <Button size="sm" variant="outline" disabled={page >= txns.pages} onClick={() => setPage(p => p + 1)}>Weiter</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Subscription Tab ────────────────────────────────────────────────

const SubscriptionTab = ({ sub, onUpgrade, onCancel }) => {
  if (!sub || !sub.has_site) {
    return (
      <Card><CardContent className="py-12 text-center text-muted-foreground">
        Kein Standort zugeordnet. Bitte Admin kontaktieren.
      </CardContent></Card>
    );
  }

  const state = sub.subscription_state || 'STANDARD_FREE';
  const cfg = stateConfig[state] || stateConfig.STANDARD_FREE;
  const StateIcon = cfg.icon;
  const tier = sub.subscription_tier;
  const isActive = state === 'ACTIVE_PREMIUM';
  const isFree = state === 'STANDARD_FREE';
  const canCancel = ['ACTIVE_PREMIUM', 'TRIAL'].includes(state);

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card className="border-2 border-orange-200 dark:border-orange-800">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${cfg.color}`}>
                <StateIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold font-manrope">{sub.site_name}</h3>
                <Badge className={`mt-1 ${cfg.color}`} data-testid="subscription-state-badge">{cfg.label}</Badge>
              </div>
            </div>
            <div className="text-right">
              {tier && (
                <>
                  <p className="text-2xl font-bold font-mono text-orange-600">
                    {sub.grandfathered_price !== null && sub.grandfathered_price !== undefined
                      ? `${sub.grandfathered_price.toFixed(2)} EUR`
                      : `${(tier.price_monthly_net || 0).toFixed(2)} EUR`}
                  </p>
                  <p className="text-xs text-muted-foreground">pro Monat (netto)</p>
                  {sub.grandfathered_price !== null && sub.grandfathered_price !== undefined && (
                    <Badge variant="outline" className="mt-1 text-xs border-amber-300 text-amber-600">Bestandspreis</Badge>
                  )}
                </>
              )}
              {!tier && <p className="text-lg font-bold text-emerald-600">Kostenlos</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tier Details */}
      {tier && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Tag className="h-4 w-4" />Ihr Abo: {tier.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Provisionssatz</p>
                <p className="font-mono font-bold text-orange-600">{tier.commission_rate}%</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Inserate-Limit</p>
                <p className="font-semibold">{tier.listing_limit || 'Unbegrenzt'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Priorität in Suche</p>
                <p className="font-semibold">{tier.priority_placement ? 'Ja' : 'Nein'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Priority Support</p>
                <p className="font-semibold">{tier.priority_support ? 'Ja' : 'Nein'}</p>
              </div>
            </div>
            {tier.features?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-2">Enthaltene Features:</p>
                <div className="flex flex-wrap gap-2">
                  {tier.features.map((f, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Group Info */}
      {sub.group_info && (
        <Card className="border-violet-200 dark:border-violet-800">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <Building className="h-5 w-5 text-violet-500" />
              <div>
                <p className="font-semibold">Gruppenabonnement: {sub.group_info.group_name}</p>
                <p className="text-xs text-muted-foreground">
                  Ihr Standort ist Teil einer Gruppe.
                  {sub.group_info.group_tier && ` Gruppen-Tier: ${sub.group_info.group_tier.label}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upgrade Options */}
      {(isFree || state === 'DOWNGRADED' || state === 'CANCELLED_EXPIRED') && sub.available_tiers?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ArrowUpCircle className="h-4 w-4 text-emerald-500" />Premium aktivieren</CardTitle>
            <CardDescription>Wählen Sie einen Tier und aktivieren Sie Ihr Abonnement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {sub.available_tiers.filter(t => t.price_monthly_net > 0).map(t => (
                <div key={t.tier_id} className="border rounded-lg p-4 hover:border-orange-300 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{t.label}</h4>
                      <p className="text-xs text-muted-foreground">{t.commission_rate}% Provision</p>
                    </div>
                    <p className="text-lg font-bold font-mono text-orange-600">{t.price_monthly_net} EUR<span className="text-xs font-normal text-muted-foreground">/Mo.</span></p>
                  </div>
                  {t.features?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {t.features.map((f, i) => <Badge key={i} variant="outline" className="text-xs">{f}</Badge>)}
                    </div>
                  )}
                  <Button size="sm" className="w-full bg-orange-500 hover:bg-orange-600" onClick={() => onUpgrade(t)} data-testid={`activate-btn-${t.name}`}>
                    <ShieldCheck className="h-4 w-4 mr-1" />Jetzt aktivieren
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancel */}
      {canCancel && (
        <Card className="border-red-100">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Abonnement kündigen</p>
                <p className="text-xs text-muted-foreground">Zugang bleibt bis Ende der Abrechnungsperiode</p>
              </div>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={onCancel} data-testid="cancel-subscription-btn">
                <XCircle className="h-4 w-4 mr-1" />Kündigen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Payments */}
      {sub.recent_payments?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><CreditCard className="h-4 w-4" />Letzte Zahlungen</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sub.recent_payments.map(p => (
                <div key={p.transaction_id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">{p.tier_name || 'Zahlung'}</p>
                    <p className="text-xs text-muted-foreground">{(p.created_at || '').substring(0, 10)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold">{(p.amount || 0).toFixed(2)} EUR</p>
                    <Badge variant="outline" className={p.payment_status === 'paid' ? 'text-emerald-600 border-emerald-200' : 'text-amber-600 border-amber-200'}>
                      {p.payment_status === 'paid' ? 'Bezahlt' : p.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── Invoices Tab ────────────────────────────────────────────────────

const InvoicesTab = ({ invoices }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" />Rechnungen</CardTitle>
      <CardDescription>{invoices.length} Rechnungen</CardDescription>
    </CardHeader>
    <CardContent>
      {invoices.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">Noch keine Rechnungen vorhanden</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="pb-2 font-medium text-muted-foreground">Nr.</th>
                <th className="pb-2 font-medium text-muted-foreground">Datum</th>
                <th className="pb-2 font-medium text-muted-foreground">Zeitraum</th>
                <th className="pb-2 font-medium text-muted-foreground">Netto</th>
                <th className="pb-2 font-medium text-muted-foreground">MwSt.</th>
                <th className="pb-2 font-medium text-muted-foreground">Brutto</th>
                <th className="pb-2 font-medium text-muted-foreground">Status</th>
                <th className="pb-2 font-medium text-muted-foreground"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.invoice_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <td className="py-2.5 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="py-2.5 text-xs text-muted-foreground">{inv.invoice_date}</td>
                  <td className="py-2.5 text-xs">{inv.billing_period_start} — {inv.billing_period_end}</td>
                  <td className="py-2.5 font-mono">{(inv.total_net || 0).toFixed(2)} EUR</td>
                  <td className="py-2.5 font-mono text-muted-foreground">{(inv.vat_amount || 0).toFixed(2)} EUR</td>
                  <td className="py-2.5 font-mono font-bold">{(inv.total_gross || 0).toFixed(2)} EUR</td>
                  <td className="py-2.5">
                    <Badge variant="outline" className={inv.payment_status === 'PAID' ? 'text-emerald-600 border-emerald-200' : 'text-amber-600 border-amber-200'}>
                      {inv.payment_status === 'PAID' ? 'Bezahlt' : inv.payment_status === 'OPEN' ? 'Offen' : inv.payment_status}
                    </Badge>
                  </td>
                  <td className="py-2.5">
                    <Button size="sm" variant="ghost" onClick={() => window.open(`${API}/invoices/${inv.invoice_id}/pdf`, '_blank')} data-testid={`download-invoice-${inv.invoice_number}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

// ─── Billing Settings Tab ────────────────────────────────────────────

const BillingSettingsTab = ({ billing, onSave, saving }) => {
  const [form, setForm] = useState(billing || {});
  useEffect(() => { if (billing) setForm(billing); }, [billing]);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-6">
      {/* SEPA Mandate Download */}
      <Card className="border-orange-200 dark:border-orange-800">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold">SEPA-Lastschriftmandat</p>
                <p className="text-xs text-muted-foreground">PDF-Mandat herunterladen, unterschreiben und an uns zurücksenden</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="gap-2 border-orange-200 text-orange-600 hover:bg-orange-50"
              onClick={() => window.open(`${API}/portal/my-subscription/sepa-mandate`, '_blank')}
              data-testid="download-sepa-mandate-btn"
            >
              <Download className="h-4 w-4" />SEPA-Mandat PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Building className="h-5 w-5" />Rechnungsdaten</CardTitle>
          <CardDescription>Diese Daten erscheinen auf Ihren Rechnungen und im SEPA-Mandat</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Firma</Label>
              <Input value={form.legal_company_name || ''} onChange={e => set('legal_company_name', e.target.value)} data-testid="billing-company" />
            </div>
            <div>
              <Label>Strasse</Label>
              <Input value={form.billing_address_street || ''} onChange={e => set('billing_address_street', e.target.value)} data-testid="billing-street" />
            </div>
            <div>
              <Label>Hausnummer</Label>
              <Input value={form.billing_address_house_number || ''} onChange={e => set('billing_address_house_number', e.target.value)} data-testid="billing-house" />
            </div>
            <div>
              <Label>PLZ</Label>
              <Input value={form.billing_address_postal_code || ''} onChange={e => set('billing_address_postal_code', e.target.value)} data-testid="billing-plz" />
            </div>
            <div>
              <Label>Stadt</Label>
              <Input value={form.billing_address_city || ''} onChange={e => set('billing_address_city', e.target.value)} data-testid="billing-city" />
            </div>
            <div>
              <Label>Land</Label>
              <Input value={form.billing_address_country || ''} onChange={e => set('billing_address_country', e.target.value)} data-testid="billing-country" />
            </div>
            <div>
              <Label>USt-ID</Label>
              <Input value={form.vat_id || ''} onChange={e => set('vat_id', e.target.value)} placeholder="z.B. DE123456789" data-testid="billing-vat" />
            </div>
            <div>
              <Label>PO-Nummer</Label>
              <Input value={form.po_number || ''} onChange={e => set('po_number', e.target.value)} data-testid="billing-po" />
            </div>
            <div>
              <Label>Rechnungskontakt E-Mail</Label>
              <Input type="email" value={form.billing_contact_email || ''} onChange={e => set('billing_contact_email', e.target.value)} data-testid="billing-email" />
            </div>
            <div>
              <Label>Rechnungskontakt Name</Label>
              <Input value={form.billing_contact_name || ''} onChange={e => set('billing_contact_name', e.target.value)} data-testid="billing-contact-name" />
            </div>
          </div>

          {/* IBAN / BIC Section */}
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm font-semibold mb-1 flex items-center gap-2"><CreditCard className="h-4 w-4 text-muted-foreground" />Bankverbindung (für SEPA-Lastschrift)</p>
            <p className="text-xs text-muted-foreground mb-4">Wird im SEPA-Mandat vorausgefüllt</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>IBAN</Label>
                <Input
                  value={form.iban || ''}
                  onChange={e => set('iban', e.target.value.replace(/\s/g, '').toUpperCase())}
                  placeholder="DE00 0000 0000 0000 0000 00"
                  className="font-mono"
                  data-testid="billing-iban"
                />
              </div>
              <div>
                <Label>BIC / SWIFT</Label>
                <Input
                  value={form.bic || ''}
                  onChange={e => set('bic', e.target.value.toUpperCase())}
                  placeholder="z.B. COBADEFFXXX"
                  className="font-mono"
                  data-testid="billing-bic"
                />
              </div>
            </div>
          </div>

          <Button className="mt-6 bg-orange-500 hover:bg-orange-600" onClick={() => onSave(form)} disabled={saving} data-testid="save-billing-btn">
            {saving ? 'Speichern...' : 'Rechnungsdaten speichern'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Invitations Tab (for seller, show pending invitations) ──────────

const MyInvitationsTab = ({ invitations, onAccept, onDecline }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" />Gruppen-Einladungen</CardTitle>
      <CardDescription>Einladungen, einer Unternehmensgruppe beizutreten</CardDescription>
    </CardHeader>
    <CardContent>
      {invitations.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">Keine Einladungen vorhanden</p>
      ) : (
        <div className="space-y-3">
          {invitations.map(inv => (
            <div key={inv.invitation_id} className="border rounded-lg p-4" data-testid={`invitation-${inv.invitation_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold">{inv.group_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Von {inv.inviting_user_name} am {(inv.sent_at || '').substring(0, 10)}
                  </p>
                  {inv.group_tier_label && (
                    <p className="text-xs mt-1">Tier: <Badge variant="secondary" className="text-xs">{inv.group_tier_label}</Badge></p>
                  )}
                  {inv.group_commission_rate && (
                    <p className="text-xs text-muted-foreground">Provisionssatz: {inv.group_commission_rate}%</p>
                  )}
                  {inv.personal_message && (
                    <p className="text-sm italic mt-2 text-muted-foreground">"{inv.personal_message}"</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {inv.status === 'PENDING' ? (
                    <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onAccept(inv)} data-testid={`accept-inv-${inv.invitation_id}`}>Annehmen</Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDecline(inv)} data-testid={`decline-inv-${inv.invitation_id}`}>Ablehnen</Button>
                    </>
                  ) : (
                    <Badge className={inv.status === 'ACCEPTED' ? 'bg-emerald-100 text-emerald-700' : inv.status === 'DECLINED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}>
                      {inv.status}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);


// ─── Main Page ───────────────────────────────────────────────────────

export default function SellerPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('subscription');
  const [loading, setLoading] = useState(true);

  // Commission state
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [poDialog, setPoDialog] = useState(false);
  const [poValue, setPoValue] = useState('');
  const [savingPO, setSavingPO] = useState(false);

  // Subscription state
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [savingBilling, setSavingBilling] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComment, setCancelComment] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [activateDialog, setActivateDialog] = useState(null);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [datenschutzAccepted, setDatenschutzAccepted] = useState(false);
  const [activating, setActivating] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, invRes, invtRes] = await Promise.all([
        axios.get(`${API}/portal/my-subscription`),
        axios.get(`${API}/portal/my-invoices`),
        axios.get(`${API}/portal/my-invitations`),
      ]);
      setSub(subRes.data);
      setInvoices(invRes.data);
      setInvitations(invtRes.data);

      // Commission data
      try {
        const [sum, txnRes] = await Promise.all([
          axios.get(`${API}/commission/seller/summary`),
          axios.get(`${API}/commission/seller/transactions`, { params: { page: 1, limit: 50 } })
        ]);
        setSummary(sum.data);
        setTxns(txnRes.data);
        setPoValue(sum.data.po_number || '');
      } catch {}
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Kein Zugang.');
        navigate('/');
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (page > 1) {
      axios.get(`${API}/commission/seller/transactions`, { params: { page, limit: 50 } })
        .then(r => setTxns(r.data)).catch(() => {});
    }
  }, [page]);

  if (!user) { navigate('/login'); return null; }

  const handleSavePO = async () => {
    setSavingPO(true);
    try {
      await axios.put(`${API}/users/me/po-number`, { po_number: poValue });
      toast.success('PO-Nummer gespeichert');
      setPoDialog(false);
      setSummary(s => ({ ...s, po_number: poValue }));
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSavingPO(false); }
  };

  const handleSaveBilling = async (form) => {
    setSavingBilling(true);
    try {
      await axios.put(`${API}/portal/my-subscription/billing`, form);
      toast.success('Rechnungsdaten gespeichert');
      setSub(prev => ({ ...prev, billing: form }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setSavingBilling(false); }
  };

  const handleUpgrade = (tier) => {
    setActivateDialog(tier);
    setAgbAccepted(false);
    setDatenschutzAccepted(false);
  };

  const handleActivate = async () => {
    if (!agbAccepted || !datenschutzAccepted) {
      toast.error('Bitte akzeptieren Sie die AGB und Datenschutzbestimmungen');
      return;
    }
    if (!activateDialog || !sub?.site_id) return;
    setActivating(true);
    try {
      await axios.post(`${API}/portal/my-subscription/activate`, {
        tier_id: activateDialog.tier_id,
        agb_accepted: true,
        datenschutz_accepted: true,
        agb_version: 'v1.0',
      });
      toast.success('Abonnement erfolgreich aktiviert!');
      setActivateDialog(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktivierung fehlgeschlagen');
    } finally { setActivating(false); }
  };

  const handleCancel = async () => {
    if (!cancelReason) { toast.error('Bitte Grund auswählen'); return; }
    setCancelling(true);
    try {
      await axios.post(`${API}/portal/my-subscription/cancel`, {
        reason: cancelReason,
        comment: cancelComment,
      });
      toast.success('Abonnement gekündigt');
      setCancelDialog(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setCancelling(false); }
  };

  const handleAcceptInvitation = async (inv) => {
    try {
      await axios.post(`${API}/invitations/${inv.invitation_id}/accept`, { confirmation: true });
      toast.success('Einladung angenommen!');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Annehmen'); }
  };

  const handleDeclineInvitation = async (inv) => {
    try {
      await axios.post(`${API}/invitations/${inv.invitation_id}/decline`, {
        reason: 'Vom Nutzer abgelehnt',
      });
      toast.success('Einladung abgelehnt');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-3 gap-4">{[0,1,2].map(i => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-96" />
      </div>
    </div>
  );

  const pendingInvitations = invitations.filter(i => i.status === 'PENDING');

  return (
    <div className="min-h-screen bg-background" data-testid="seller-portal">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold font-manrope" data-testid="seller-portal-title">Verkäufer-Portal</h1>
            <p className="text-sm text-muted-foreground mt-1">Verwalten Sie Ihr Abonnement, Rechnungen und Provisionen</p>
          </div>
          {sub?.subscription_tier && (
            <Badge className={sub.subscription_state === 'ACTIVE_PREMIUM' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700'} data-testid="seller-tier-badge">
              {sub.subscription_state === 'ACTIVE_PREMIUM' ? <><Star className="h-3 w-3 mr-1 fill-current" />Premium</> : 'Standard'}
            </Badge>
          )}
        </div>

        {/* Pending Invitations Banner */}
        {pendingInvitations.length > 0 && (
          <div className="mb-4 p-4 bg-violet-50 dark:bg-violet-950 border-2 border-violet-200 dark:border-violet-800 rounded-xl" data-testid="invitation-banner">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-violet-600" />
              <div>
                <p className="font-semibold text-violet-700 dark:text-violet-300">{pendingInvitations.length} offene Gruppen-Einladung{pendingInvitations.length > 1 ? 'en' : ''}</p>
                <p className="text-xs text-violet-600 dark:text-violet-400">Wechseln Sie zum Tab "Einladungen" um zu antworten</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 grid w-full grid-cols-6">
            <TabsTrigger value="subscription" data-testid="tab-subscription">Abonnement</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Rechnungen</TabsTrigger>
            <TabsTrigger value="commission" data-testid="tab-commission">Provisionen</TabsTrigger>
            <TabsTrigger value="invitations" data-testid="tab-invitations" className="relative">
              Einladungen
              {pendingInvitations.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-violet-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">{pendingInvitations.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="integration" data-testid="tab-integration"><Plug className="h-4 w-4 mr-1 inline" />Schnittstelle</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">Einstellungen</TabsTrigger>
          </TabsList>

          <TabsContent value="subscription">
            <SubscriptionTab sub={sub} onUpgrade={handleUpgrade} onCancel={() => setCancelDialog(true)} />
          </TabsContent>

          <TabsContent value="invoices">
            <InvoicesTab invoices={invoices} />
          </TabsContent>

          <TabsContent value="commission">
            <CommissionTab
              summary={summary}
              txns={txns}
              page={page}
              setPage={setPage}
              poDialog={poDialog}
              setPoDialog={setPoDialog}
              poValue={poValue}
              setPoValue={setPoValue}
              handleSavePO={handleSavePO}
              savingPO={savingPO}
            />
          </TabsContent>

          <TabsContent value="invitations">
            <MyInvitationsTab invitations={invitations} onAccept={handleAcceptInvitation} onDecline={handleDeclineInvitation} />
          </TabsContent>

          <TabsContent value="integration">
            <ApiIntegrationTab siteId={user?.site_id} />
          </TabsContent>

          <TabsContent value="billing">
            <BillingSettingsTab billing={sub?.billing} onSave={handleSaveBilling} saving={savingBilling} />
          </TabsContent>
        </Tabs>
      </div>

      {/* PO Edit Dialog */}
      <Dialog open={poDialog} onOpenChange={setPoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>PO-Nummer bearbeiten</DialogTitle>
            <DialogDescription>Wird auf alle Provisionsberichte gedruckt</DialogDescription>
          </DialogHeader>
          <Input placeholder="z.B. PO-2025-001" value={poValue} onChange={e => setPoValue(e.target.value)} data-testid="po-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSavePO} disabled={savingPO} data-testid="save-po-btn">{savingPO ? 'Speichern...' : 'Speichern'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Abonnement kündigen</DialogTitle>
            <DialogDescription>Ihr Zugang bleibt bis zum Ende der aktuellen Abrechnungsperiode bestehen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Kündigungsgrund</Label>
              <Select value={cancelReason} onValueChange={setCancelReason}>
                <SelectTrigger data-testid="cancel-reason-select">
                  <SelectValue placeholder="Bitte auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="too_expensive">Zu teuer</SelectItem>
                  <SelectItem value="not_enough_value">Nicht genug Mehrwert</SelectItem>
                  <SelectItem value="switching_provider">Wechsel zu anderem Anbieter</SelectItem>
                  <SelectItem value="company_closed">Unternehmen geschlossen</SelectItem>
                  <SelectItem value="other">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kommentar (optional)</Label>
              <Textarea value={cancelComment} onChange={e => setCancelComment(e.target.value)} placeholder="Ihr Feedback hilft uns..." data-testid="cancel-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleCancel} disabled={cancelling} data-testid="confirm-cancel-btn">
              {cancelling ? 'Kündigen...' : 'Endgültig kündigen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activation Dialog */}
      <Dialog open={!!activateDialog} onOpenChange={(v) => !v && setActivateDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-orange-500" />
              Abonnement aktivieren
            </DialogTitle>
            <DialogDescription>
              Aktivieren Sie Ihr Premium-Abonnement für <span className="font-semibold">{sub?.site_name}</span>
            </DialogDescription>
          </DialogHeader>
          {activateDialog && (
            <div className="space-y-5">
              <div className="border rounded-lg p-4 bg-orange-50/50 dark:bg-orange-950/20">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-lg">{activateDialog.label}</p>
                    <p className="text-sm text-muted-foreground">Provisionssatz: {activateDialog.commission_rate}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold font-mono text-orange-600">{activateDialog.price_monthly_net} EUR</p>
                    <p className="text-xs text-muted-foreground">pro Monat (netto)</p>
                  </div>
                </div>
                {activateDialog.features?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t">
                    {activateDialog.features.map((f, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3 border rounded-lg p-4">
                <p className="text-sm font-semibold">Zustimmung erforderlich</p>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="agb-checkbox-label">
                  <input
                    type="checkbox"
                    checked={agbAccepted}
                    onChange={(e) => setAgbAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 accent-orange-500"
                    data-testid="agb-checkbox"
                  />
                  <span className="text-sm">
                    Ich habe die <a href="/agb" target="_blank" rel="noopener" className="text-orange-600 underline underline-offset-2 hover:text-orange-700">Allgemeinen Geschäftsbedingungen (AGB)</a> gelesen und akzeptiere diese.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="datenschutz-checkbox-label">
                  <input
                    type="checkbox"
                    checked={datenschutzAccepted}
                    onChange={(e) => setDatenschutzAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 accent-orange-500"
                    data-testid="datenschutz-checkbox"
                  />
                  <span className="text-sm">
                    Ich habe die <a href="/datenschutz" target="_blank" rel="noopener" className="text-orange-600 underline underline-offset-2 hover:text-orange-700">Datenschutzbestimmungen</a> gelesen und akzeptiere diese.
                  </span>
                </label>
              </div>

              <p className="text-xs text-muted-foreground">
                Mit der Aktivierung wird eine Rechnung erstellt. Die Zahlung erfolgt per SEPA-Lastschrift.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivateDialog(null)}>Abbrechen</Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600"
              onClick={handleActivate}
              disabled={activating || !agbAccepted || !datenschutzAccepted}
              data-testid="confirm-activate-btn"
            >
              {activating ? 'Aktivierung...' : 'Kostenpflichtig aktivieren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
